import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import API from "../../../util/api";
import ConfirmationModal from "../../Common/ConfirmationModal";
import { actionContainerStyles, actionLinkStyles, actionHeaderStyles } from "../common/ActionStyles";
import ResponsiveActions from "../common/ResponsiveActions";
import {
  adminTable,
  adminTableHeaderCell,
  adminTableDataCell,
} from "../common/AdminTableStyles";

const LINK_HOVER_COLOR = "#F7941D";
const PAGE_SIZE_DEFAULT = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const SORT_FIELDS = [
  { value: "announcement_date", label: "Announcement Date" },
  { value: "inserted", label: "Inserted Date" },
  { value: "title", label: "Title" },
];
const SORT_ORDERS = [
  { value: "desc", label: "Latest to Oldest" },
  { value: "asc", label: "Oldest to Latest" },
];
const breakpoints = {
  phone: 480,
};

// ── Styled Components ───────────────────────────────────────────────────────

const Container = styled.div`
  padding: 1.5em;
  background: transparent;
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: stretch;
`;

const Actions = styled.div`
  ${actionContainerStyles};
  // border: 1px solid ${({ colors }) => colors.border};

  h3 {
    ${actionHeaderStyles};
    border-bottom: 1px solid ${({ colors }) => colors.border};
  }

  ul {
    margin: 0;
    list-style: none;
    padding: 12px 16px;
  }

  li + li {
    margin-top: 4px;
  }

  a {
    ${actionLinkStyles};
    padding-left: 16px;
    color: #2C4960;

    &:hover {
      color: ${LINK_HOVER_COLOR};
    }
  }
  @media (min-width: 768px) {
    flex: 0 0 260px;
  }
`;

const View = styled.div`
  flex: 1 1 0;
  min-width: 0;
  width: 100%;
`;

const Table = styled.table`
  ${adminTable};
  font-size: 14px;
  table-layout: auto;

  thead th {
    ${adminTableHeaderCell};
    background-color: #fff;

    &.announcementHeading {
      width: 75%;
    }

    &.actionsHeading {
      width: 1%;
      white-space: nowrap;
      text-align: right;
    }
  }

  tbody tr {
    ${'' /* ensure rows match requested styling */}
    text-align: left;
    color: #333;
    border-spacing: 0;
    font-size: 12px;
    font-family: "Source Sans Pro", sans-serif;
  }

  tbody td {
    ${adminTableDataCell};
    padding: 10px;
    // background: #f5f5f5;
    vertical-align: top;

    &.announcementCell {
      width: 100%;
    }

    &.actionsCell {
      // background: #fff;
      text-align: right;
    }
  }
`;

const AnnList = styled.ul`
  margin: 0;
  padding-left: 1em;
  li {
    margin-bottom: 0.5em;
  }
`;

const TitleLink = styled.a`
  color: ${({ colors }) => colors.buttonBackground};
  text-decoration: none;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
  strong {
    display: block;
    margin-bottom: 0.2em;
  }
`;

const LinkButton = styled.button`
  display: inline-block;
  padding: 5px 12px;
  background: ${({ colors }) => colors.buttonBackground};
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9em;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  & + & {
    margin-left: 6px;
  }
`;

const ActionButtons = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
`;

const Form = styled.form`
  width: 100%;
`;

const Field = styled.div`
  margin-bottom: 1em;
  label {
    display: block;
    margin-bottom: 0.3em;
    font-size: 0.95em;
    color: ${({ colors }) => colors.labelText};
  }
  input[type="text"],
  textarea {
    width: 100%;
    padding: 0.5em;
    border: 1px solid ${({ colors }) => colors.border};
    border-radius: 4px;
    font-family: inherit;
    font-size: 1em;
  }
  textarea {
    resize: vertical;
    min-height: 100px;
  }
`;

const SubmitRow = styled.div`
  margin-top: 1.5em;
  input[type="submit"] {
    padding: 0.6em 1.2em;
    background: ${({ colors }) => colors.buttonBackground};
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 1em;
    cursor: pointer;
    &:hover {
      background: ${({ colors }) => colors.buttonHoverBackground};
    }
  }
  a.cancel {
    margin-left: 1em;
    font-size: 0.95em;
    color: ${({ colors }) => colors.labelText};
    text-decoration: none;
    cursor: pointer;
    &:hover {
      color: ${({ colors }) => colors.buttonBackground};
    }
  }
`;

const TableControls = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: ${breakpoints.phone}px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const ControlGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${({ colors }) => colors.labelText};

  label {
    font-weight: 600;
  }

  select {
    padding: 4px 8px;
    border: 1px solid ${({ colors }) => colors.border};
    border-radius: 4px;
    font-size: 14px;
  }
`;

const PaginationInfo = styled.div`
  margin: 10px 0;
  font-size: 12px;
  color: ${({ colors }) => colors.labelText};

  @media (max-width: ${breakpoints.phone}px) {
    text-align: center;
    font-size: 11px;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;

  button {
    padding: 5px 10px;
    margin-left: 5px;
    border: 1px solid #ccc;
    background-color: #f1f1f1;
    border-radius: 5px;
    cursor: pointer;

    &:disabled {
      background-color: #e6e6e6;
      cursor: not-allowed;
    }

    &:hover:not(:disabled) {
      background-color: #ddd;
    }
  }

  @media (max-width: ${breakpoints.phone}px) {
    justify-content: center;
    flex-wrap: wrap;
    gap: 8px;

    button {
      padding: 6px 12px;
      margin-left: 0;
      margin: 4px;
      font-size: 12px;
    }
  }
`;


export default function OtherAnnouncementsContent({ colors }) {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [sortField, setSortField] = useState("announcement_date");
  const [sortOrder, setSortOrder] = useState("desc");

  // viewMode: "list" | "form" | "view"
  const [viewMode, setViewMode] = useState("list");
  const [currentAnn, setCurrentAnn] = useState(null);

  // form fields
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);

  // confirm modals
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalMessage, setModalMessage] = useState("");
  const [modalStatus, setModalStatus] = useState("Success");
  const [pendingAction, setPendingAction] = useState(null);       
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  const today = new Date().toISOString().slice(0, 10);
  const sortedAnnouncements = useMemo(() => {
    const getComparableValue = (ann, field) => {
      if (field === "title") {
        return (ann.title || "").toLowerCase();
      }
      if (field === "announcement_date" || field === "inserted") {
        const value = ann[field] || "";
        const timestamp = value ? new Date(value).getTime() : 0;
        return Number.isNaN(timestamp) ? 0 : timestamp;
      }
      return ann[field] || "";
    };

    const copy = [...announcements];
    copy.sort((a, b) => {
      const valueA = getComparableValue(a, sortField);
      const valueB = getComparableValue(b, sortField);
      if (valueA < valueB) return sortOrder === "asc" ? -1 : 1;
      if (valueA > valueB) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [announcements, sortField, sortOrder]);

  const totalAnnouncements = sortedAnnouncements.length;
  const pageCount = totalAnnouncements
    ? Math.ceil(totalAnnouncements / pageSize)
    : 1;
  const startIndex = totalAnnouncements ? (currentPage - 1) * pageSize : 0;
  const paginatedAnnouncements = sortedAnnouncements.slice(
    startIndex,
    startIndex + pageSize
  );
  const showingFrom = totalAnnouncements ? startIndex + 1 : 0;
  const showingTo = Math.min(startIndex + pageSize, totalAnnouncements);

  useEffect(() => {
    setCurrentPage((prev) => Math.min(prev, pageCount));
  }, [pageCount]);

  const load = async () => {
    setLoading(true);
    try {
      const resp = await API.getAdminAnnouncements({});
      setAnnouncements(resp.announcements || []);
      setCurrentPage(1);
    } catch (err) {
      console.error("Failed to load announcements:", err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <p>Loading…</p>;

  if (viewMode === "view" && currentAnn) {
    const [ymd] = currentAnn.announcement_date.split(" ");
    const [yyyy, mm, dd] = ymd.split("-");
    const dateObj = new Date(+yyyy, +mm - 1, +dd);
    const dateOnly = dateObj.toLocaleDateString("en-US", {
      month: "short",
      day:   "numeric",
    });
    return (
      <Container>
        <div id="bigbox">
          <strong>{currentAnn.title}</strong>{" "}
          <small>{dateOnly}</small>
          <br />
          <div
            style={{ textAlign: "justify" }}
            dangerouslySetInnerHTML={{ __html: currentAnn.announcement }}
          />
          <br />
          <p>
            <a onClick={() => setViewMode("list")}>
              &lt;&lt; Back to announcements
            </a>
          </p>
          <div style={{ clear: "both" }} />
        </div>
      </Container>
    );
  }

  const startCreate = () => {
    setEditingAnnouncement(null);
    setCurrentAnn(null);
    setTitle("");
    setDescription("");
    setDate(today);
    setViewMode("form");
  };

  const startEdit = (announcement) => {
    setEditingAnnouncement(announcement);
    setCurrentAnn(null);
    setTitle(announcement.title || "");
    setDescription(announcement.announcement || "");
    const [datePart] = (announcement.announcement_date || "").split(" ");
    setDate(datePart || today);
    setViewMode("form");
  };

  const handleConfirm = async () => {
    setConfirmOpen(false);
    setModalStatus("Success");
    if (pendingAction === "create") {
      try {
        const resp = await API.addAdminAnnouncements({
          title,
          announcement: description,
          date: `${date} 00:00:00`,
        });
        setModalTitle(resp?.status === "Failed" ? "Error" : "Success");
        setModalMessage(resp?.message || "Announcement created.");
        setModalStatus(resp?.status === "Failed" ? "Failed" : "Success");
        // reset form
        setViewMode("list");
        setTitle("");
        setDescription("");
        setDate(today);
        setEditingAnnouncement(null);
        await load();
      } catch (err) {
        console.error(err);
        setModalTitle("Error");
        setModalMessage("Failed to create announcement.");
        setModalStatus("Failed");
      }
    } else if (pendingAction === "edit" && editingAnnouncement) {
      try {
        const resp = await API.editAdminAnnouncement({
          announcement_id: editingAnnouncement.announcement_id,
          title,
          announcement: description,
          date: `${date} 00:00:00`,
        });
        setModalTitle(resp?.status === "Failed" ? "Error" : "Success");
        setModalMessage(resp?.message || "Announcement updated.");
        setModalStatus(resp?.status === "Failed" ? "Failed" : "Success");
        setViewMode("list");
        setEditingAnnouncement(null);
        setTitle("");
        setDescription("");
        setDate(today);
        await load();
      } catch (err) {
        console.error(err);
        setModalTitle("Error");
        setModalMessage("Failed to update announcement.");
        setModalStatus("Failed");
      }
    } else {
      try {
        const resp = await API.deleteAdminAnnouncements({ announcement_id: pendingDeleteId });
        setModalTitle(resp?.status === "Failed" ? "Error" : "Deleted");
        setModalMessage(resp?.message || "Announcement removed.");
        setModalStatus(resp?.status === "Failed" ? "Failed" : "Success");
        await load();
      } catch (err) {
        console.error(err);
        setModalTitle("Error");
        setModalMessage("Failed to delete announcement.");
        setModalStatus("Failed");
      }
    }
    setResultOpen(true);
    setPendingAction(null);
    setPendingDeleteId(null);
  };

  const handleResultClose = () => {
    setResultOpen(false);
    setModalStatus("Success");
  };

  return (
    <Container>
      {/* Confirm Modal */}
      <ConfirmationModal
        show={confirmOpen}
        title="Please confirm"
        message={
          pendingAction === "create"
            ? "Create this announcement?"
            : pendingAction === "edit"
            ? "Update this announcement?"
            : "Delete this announcement?"
        }
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
      />
      {/* Result Modal */}
      <ConfirmationModal
        show={resultOpen}
        title={modalTitle}
        message={modalMessage}
        onCancel={handleResultClose}
        onConfirm={handleResultClose}
        status={modalStatus}
      />

      {/* Actions Sidebar */}
      <ResponsiveActions
        colors={colors}
        renderActions={() => {
          if (viewMode === "list") {
            return (
              <Actions colors={colors}>
                <h3>Actions</h3>
                <ul>
                  <li>
                    <a onClick={startCreate}>Add New</a>
                  </li>
                </ul>
              </Actions>
            );
          }

          return (
            <Actions colors={colors}>
              <h3>Actions</h3>
              <ul>
                <li>
                  <a
                    onClick={() => {
                      setViewMode("list");
                      setEditingAnnouncement(null);
                      setCurrentAnn(null);
                    }}
                  >
                    Back to List
                  </a>
                </li>
              </ul>
            </Actions>
          );
        }}
      />

      <View fullWidth={viewMode !== "list"}>
        {viewMode === "form" ? (
          <>
            <h1 style={{ color: colors.labelText }}>
              {editingAnnouncement ? "Edit Announcement" : "Adding A New Announcement"}
            </h1>
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                setPendingAction(editingAnnouncement ? "edit" : "create");
                setConfirmOpen(true);
              }}
            >
              <Field colors={colors}>
                <label htmlFor="annTitle">Title</label>
                <input
                  id="annTitle"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                />
              </Field>
              <Field colors={colors}>
                <label htmlFor="annDesc">Announcement</label>
                <textarea
                  id="annDesc"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                />
              </Field>
              <Field colors={colors}>
                <label htmlFor="annDate">Date</label>
                <input
                  id="annDate"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  required
                />
              </Field>
              <SubmitRow colors={colors}>
                <input type="submit" value="Submit" />
                <a
                  className="cancel"
                  onClick={() => {
                    setViewMode("list");
                    setEditingAnnouncement(null);
                    setCurrentAnn(null);
                  }}
                >
                  Cancel
                </a>
              </SubmitRow>
            </Form>
          </>
        ) : (
          <>
            <TableControls>
              <ControlGroup colors={colors}>
                <label htmlFor="announcementPageSize">Show</label>
                <select
                  id="announcementPageSize"
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
                <span>entries</span>
              </ControlGroup>
              <ControlGroup colors={colors}>
                <label htmlFor="announcementSortField">Sort by</label>
                <select
                  id="announcementSortField"
                  value={sortField}
                  onChange={(e) => {
                    setSortField(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  {SORT_FIELDS.map((field) => (
                    <option key={field.value} value={field.value}>
                      {field.label}
                    </option>
                  ))}
                </select>
                <select
                  id="announcementSortOrder"
                  value={sortOrder}
                  onChange={(e) => {
                    setSortOrder(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  {SORT_ORDERS.map((order) => (
                    <option key={order.value} value={order.value}>
                      {order.label}
                    </option>
                  ))}
                </select>
              </ControlGroup>
            </TableControls>
            <Table colors={colors}>
              <thead>
                <tr>
                  <th className="announcementHeading">Announcement</th>
                  <th className="actionsHeading">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedAnnouncements?.map((a) => {
                // prepare for list snippet
                const [ymd] = a.announcement_date.split(" ");
                const [yyyy, mm, dd] = ymd.split("-");
                const dateObj = new Date(+yyyy, +mm - 1, +dd);
                const dateOnly = dateObj.toLocaleDateString("en-US", {
                  month: "short",
                  day:   "numeric",
                });
                return (
                  <tr key={a.announcement_id}>
                    <td className="announcementCell">
                      <AnnList>
                        <li>
                          <TitleLink
                            colors={colors}
                            onClick={() => {
                              setCurrentAnn(a);
                              setViewMode("view");
                            }}
                          >
                            <strong>{a.title}</strong>
                            <small> {dateOnly}</small>
                            <br />
                            {a.announcement}
                          </TitleLink>
                        </li>
                      </AnnList>
                    </td>
                    <td className="actionsCell">
                      <ActionButtons>
                        <LinkButton
                          colors={colors}
                          onClick={() => startEdit(a)}
                        >
                          Edit
                        </LinkButton>
                        <LinkButton
                          colors={colors}
                          onClick={() => {
                            setPendingAction("delete");
                            setPendingDeleteId(a.announcement_id);
                            setConfirmOpen(true);
                          }}
                        >
                          Delete
                        </LinkButton>
                      </ActionButtons>
                    </td>
                  </tr>
                );
                })}
              </tbody>
            </Table>
            <PaginationInfo colors={colors}>
              Showing {showingFrom} to {showingTo} of {totalAnnouncements} entries
            </PaginationInfo>
            <Pagination colors={colors}>
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.min(p + 1, pageCount))}
                disabled={currentPage === pageCount || totalAnnouncements === 0}
              >
                Next
              </button>
            </Pagination>
          </>
        )}
      </View>
      <div style={{ clear: "both" }} />
    </Container>
  );
}
